import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../services/authService';
import TimeLogsService, { CreateTimeLogData } from '../services/timelogsService';
import { goalService } from '../services/goalService';
import { taskService, Task } from '../services/taskService';
import { habitService, Habit } from '../services/habitService';
import { Goal } from '../types/Goal';
import './Timer.css';

// Unified interface for activities
interface Activity {
  id: string | number;
  name: string;
  type: 'goal' | 'task' | 'habit';
}

interface TimerProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface TimerState {
  isRunning: boolean;
  startTime: number | null;
  elapsedTime: number;
  activityType: 'goal' | 'task' | 'habit' | '';
  activityId: string;
  activityName: string;
  description: string;
}

const Timer: React.FC<TimerProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTimerSection, setShowTimerSection] = useState(true);
  
  // State for existing activities
  const [goals, setGoals] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  const [timer, setTimer] = useState<TimerState>({
    isRunning: false,
    startTime: null,
    elapsedTime: 0,
    activityType: '',
    activityId: '',
    activityName: '',
    description: ''
  });


  // Load existing activities
  const loadActivities = async () => {
    if (!user) return;
    
    try {
      setLoadingActivities(true);
      
      // Load goals (no limit to get all goals)
      const goalsResponse = await goalService.getUserGoals({ 
        user_id: user.id.toString()
      });
      if (goalsResponse.success && goalsResponse.goals) {
        // Sort goals alphabetically by title for easier selection
        const sortedGoals = [...goalsResponse.goals].sort((a, b) => 
          (a.title || '').localeCompare(b.title || '')
        );
        setGoals(sortedGoals);
      }
      
      // Load tasks
      const tasksResponse = await taskService.getUserTasks({ 
        user_id: user.id.toString(), 
        limit: 100 
      });
      if (tasksResponse.success && tasksResponse.tasks) {
        setTasks(tasksResponse.tasks);
      }
      
      // Load habits
      const habitsResponse = await habitService.getUserHabits(user.id);
      if (habitsResponse.success && habitsResponse.habits) {
        setHabits(habitsResponse.habits);
      }
    } catch (err: any) {
      console.error('Error loading activities:', err);
      setError('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  // Load activities on component mount
  useEffect(() => {
    loadActivities();
  }, [user]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timer.isRunning && timer.startTime) {
      interval = setInterval(() => {
        setTimer(prev => ({
          ...prev,
          elapsedTime: Date.now() - prev.startTime!
        }));
      }, 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer.isRunning, timer.startTime]);

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    if (!timer.activityType || !timer.activityName) {
      setError('Please select an activity type and name');
      return;
    }
    
    setTimer(prev => ({
      ...prev,
      isRunning: true,
      startTime: Date.now(),
      elapsedTime: 0
    }));
    setError(null);
  };

  const stopTimer = async () => {
    if (!timer.isRunning || !user) return;
    
    const durationMinutes = Math.floor(timer.elapsedTime / 60000);
    
    if (durationMinutes < 1) {
      setError('Timer must run for at least 1 minute');
      return;
    }
    
    try {
      setLoading(true);
      
      const endTime = new Date();
      const startTime = new Date(timer.startTime!);
      
      const timeLogData: CreateTimeLogData = {
        user_id: user.id,
        activity_type: timer.activityType as 'goal' | 'task' | 'habit',
        activity_id: timer.activityId || `temp_${Date.now()}`,
        activity_name: timer.activityName,
        duration_minutes: durationMinutes,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        description: timer.description || undefined,
        is_manual_entry: false
      };
      
      await TimeLogsService.createTimeLog(timeLogData);
      
      setTimer({
        isRunning: false,
        startTime: null,
        elapsedTime: 0,
        activityType: '',
        activityId: '',
        activityName: '',
        description: ''
      });
      
      setError(null);
    } catch (err: any) {
      console.error('Error saving time log:', err);
      setError(err.message || 'Failed to save time log');
    } finally {
      setLoading(false);
    }
  };



  // Helper functions to get current activities as unified Activity objects
  const getCurrentActivities = (): Activity[] => {
    switch (timer.activityType) {
      case 'goal':
        return goals.map(goal => ({
          id: goal.id,
          name: goal.title,
          type: 'goal' as const
        }));
      case 'task':
        return tasks.map(task => ({
          id: task.id,
          name: task.title,
          type: 'task' as const
        }));
      case 'habit':
        return habits.map(habit => ({
          id: habit.id,
          name: habit.name,
          type: 'habit' as const
        }));
      default:
        return [];
    }
  };


  // Handle activity selection
  const handleActivitySelection = (activityId: string, activityName: string) => {
    setTimer(prev => ({
      ...prev,
      activityId,
      activityName
    }));
  };


  return (
    <div className="timer-container">
      <div className="timer-header">
        <div className="header-left">
          <button className="menu-toggle" onClick={onToggleSidebar}>
            ☰
          </button>
          <h1>⏱️ /timer</h1>
        </div>
        <div className="header-right">
          <button className="list-btn" onClick={() => navigate('/logs')}>📋</button>
        </div>
      </div>

      <div className="timer-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Timer Section */}
        <div className="section">
          <button 
            className="section-toggle" 
            onClick={() => setShowTimerSection(!showTimerSection)}
          >
            <span className="toggle-text">/timer</span>
            <span className="toggle-arrow">{showTimerSection ? '▼' : '▶'}</span>
          </button>
          <div className={`section-content ${showTimerSection ? 'expanded' : 'collapsed'}`}>
            <div className="timer-display-container">
              <div className="timer-display">
                <div className="timer-time">
                  {formatTime(timer.elapsedTime)}
                </div>
                <div className="timer-status">
                  {timer.isRunning ? 'Running' : 'Stopped'}
                </div>
              </div>
              
              <div className="timer-controls">
                <div className="timer-inputs">
                  <select
                    value={timer.activityType}
                    onChange={(e) => {
                      const newType = e.target.value as any;
                      setTimer(prev => ({ 
                        ...prev, 
                        activityType: newType,
                        activityId: '',
                        activityName: ''
                      }));
                    }}
                    disabled={timer.isRunning}
                  >
                    <option value="">Select activity type</option>
                    <option value="goal">Goal</option>
                    <option value="task">Task</option>
                    <option value="habit">Habit</option>
                  </select>
                  
                  {timer.activityType && (
                    <select
                      value={timer.activityId}
                      onChange={(e) => {
                        const selectedActivity = getCurrentActivities().find(
                          activity => activity.id.toString() === e.target.value
                        );
                        if (selectedActivity) {
                          handleActivitySelection(e.target.value, selectedActivity.name);
                        }
                      }}
                      disabled={timer.isRunning}
                    >
                      <option value="">Select existing {timer.activityType}</option>
                      {getCurrentActivities().map(activity => (
                        <option key={activity.id} value={activity.id.toString()}>
                          {activity.name}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <input
                    type="text"
                    placeholder={timer.activityId ? "Activity name (auto-filled)" : "Activity name"}
                    value={timer.activityName}
                    onChange={(e) => setTimer(prev => ({ ...prev, activityName: e.target.value }))}
                    disabled={timer.isRunning}
                  />
                  
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={timer.description}
                    onChange={(e) => setTimer(prev => ({ ...prev, description: e.target.value }))}
                    disabled={timer.isRunning}
                  />
                </div>
                
                <div className="timer-buttons">
                  {!timer.isRunning ? (
                    <button 
                      className="start-btn"
                      onClick={startTimer}
                      disabled={!timer.activityType || !timer.activityName}
                    >
                      ▶ Start
                    </button>
                  ) : (
                    <button 
                      className="stop-btn"
                      onClick={stopTimer}
                      disabled={loading}
                    >
                      ⏹ Stop
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default Timer;
